import { observer } from 'mobx-react-lite'
import { LockSimpleIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { useGateToggles } from 'app/hooks/useGateToggles'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import { gateFor } from 'app/lib/workflows/blockGates'
import type { GateLocation } from 'app/types/workflow-gates'
import { paletteGroups, type PaletteItem } from 'app/lib/workflows/palette'
import type { WorkflowNodeKind } from 'app/types/workflows'
import { LockPanel } from './LockPanel'

type BlockPickerProps = {
  screen: { x: number; y: number }
  /** Edit task is offered only where it has a task to edit. */
  onTaskPath: boolean
  onPick: (item: PaletteItem) => void
  onClose: () => void
  /** The workflow's location, which decides what is padlocked. Undefined locks nothing. */
  gateLocation?: GateLocation
}

type BlockKind = Exclude<WorkflowNodeKind, `trigger`>

const WIDTH = 224
const MAX_HEIGHT = 340

/** Floating menu of insertable blocks, anchored near a screen point. Used by both the
 *  connector "+" and the drag-off-a-handle interactions. */
export const BlockPicker = observer(function BlockPicker({
  screen,
  onTaskPath,
  onPick,
  onClose,
  gateLocation,
}: BlockPickerProps) {
  const toggles = useGateToggles()
  // Triggers are entry-only, so they can't be inserted mid-flow.
  const insertableGroups = paletteGroups(useListCopy()).filter((group) => group.title !== `Triggers`)

  useEscapeListener(onClose)

  // Keep the menu on-screen.
  const left = Math.min(screen.x, window.innerWidth - WIDTH - 8)
  const top = Math.min(screen.y, window.innerHeight - MAX_HEIGHT - 8)

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 overflow-y-auto rounded-lg border border-default bg-surface-raised p-1 shadow-xl"
        style={{ left, top, width: WIDTH, maxHeight: MAX_HEIGHT }}
      >
        {insertableGroups.map((group) => (
          <div key={group.title}>
            <div className="px-2 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-minimal uppercase">
              {group.title}
            </div>
            {group.items
              .filter((item) => onTaskPath || item.kind !== `editTask`)
              .map((item) => ({ item, gate: gateFor({ node: item.kind as BlockKind }, gateLocation, toggles) }))
              .filter(({ gate }) => gate.state !== `hidden`)
              .map(({ item, gate }) => {
                const locked = gate.state === `locked`
                const face = (
                  <>
                    <span
                      className={cn(
                        `flex size-6 shrink-0 items-center justify-center rounded-md`,
                        item.chip,
                        locked && `opacity-60`,
                      )}
                    >
                      <item.icon size={14} weight="bold" />
                    </span>
                    <span className={cn(`truncate text-xs font-medium`, locked ? `text-subtle` : `text-default`)}>
                      {item.label}
                    </span>
                  </>
                )
                if (gate.state !== `locked`)
                  return (
                    <Button
                      key={item.label}
                      variant="ghost"
                      size="row"
                      onClick={() => onPick(item)}
                      className="min-w-0"
                    >
                      {face}
                    </Button>
                  )
                return (
                  <Popover key={item.label}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="row"
                        className="min-w-0"
                        data-testid={`block-picker-locked-${item.kind}`}
                      >
                        {face}
                        <LockSimpleIcon size={12} weight="bold" className="ml-auto shrink-0 text-subtle" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="start" className="w-64">
                      <LockPanel gate={gate} blockLabel={item.label} blockKind={item.kind} location={gateLocation} />
                    </PopoverContent>
                  </Popover>
                )
              })}
          </div>
        ))}
      </div>
    </>
  )
})
